<template>
  <div
    ref="chartContainerRef"
    class="lw-chart relative"
    :class="'py-2'"
    @click="setSymbolChartDataLayer"
    @mouseover="handleDataLayerTimeout"
    @mouseleave="resetDataLayerTimeout"
  >
    <div
      ref="tooltipRef"
      class="absolute w-auto top-0 px-1 z-20 bg-neutral-primary pointer-events-none"
      :style="{
        left: `${shiftedCoordinateTooltip}px`,
        visibility: showTooltip ? 'visible' : 'hidden',
      }"
    >
      <NText
        v-if="tooltipDate && selectedTimespan"
        class="w-full whitespace-nowrap"
        weight="regular"
        variant="overline"
      >
        {{ d(timestampToDate(tooltipDate), getDateFormat(selectedTimespan)) }}
      </NText>
    </div>

    <div
      class="h-full absolute z-10 bg-neutral-primary opacity-75 pointer-events-none"
      :style="{
        width: `${overlayWidth}px`,
        left: `${shiftedCoordinateOverlay}px`,
        visibility: showOverlay ? 'visible' : 'hidden',
      }"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, toRefs } from 'vue';
import { LineData, Time } from 'lightweight-charts';
import { useI18n } from 'vue-i18n';
import { dataLayer } from '@libs/gtm';
import { Instrument } from '@libs/api-reference';
import {
  calcBubbleOfInvestmentFunds,
  SymbolCandleStickLineChartDataType,
  VALID_SECTOR_INVESTMENT_FUNDS,
} from '@core/symbol';

import {
  useSymbolLineChart,
  SYMBOL_LINE_CHART_FILTERS,
} from '@/modules/symbol';

type LineChartPropsType = {
  chartData: SymbolCandleStickLineChartDataType;
  selectedTimespan: string;
  referencePrice: number;
  chartPercentage: number;
  seriesColor: string;
  symbol: Instrument;
};
type LineChartEmitsType = {
  (e: 'pointClick', value: LineData): void;
  (e: 'toggleChartVisibility'): void;
};

const props = withDefaults(defineProps<LineChartPropsType>(), {
  referencePrice: 0,
  chartPercentage: 0,
  seriesColor: '#808080',
});
const emit = defineEmits<LineChartEmitsType>();

const chartContainerRef = ref<HTMLElement>();
const tooltipRef = ref<HTMLElement>();

const threeSecondsDataLayerTimeout = ref<ReturnType<typeof setTimeout> | null>(
  null
);
const fiveSecondsDataLayerTimeout = ref<ReturnType<typeof setTimeout> | null>(
  null
);

const bubblePercent = computed(() =>
  props.symbol.sectorCode &&
  VALID_SECTOR_INVESTMENT_FUNDS.includes(props.symbol.sectorCode)
    ? calcBubbleOfInvestmentFunds({
        lastTradePrice: props.symbol.lastTradePrice,
        priceOfNav: props.symbol.priceOfRedemptionNav,
      })
    : 0
);

const { d } = useI18n();

const {
  shiftedCoordinateTooltip,
  showTooltip,
  tooltipDate,
  overlayWidth,
  shiftedCoordinateOverlay,
  showOverlay,
} = useSymbolLineChart({
  chartContainerRef,
  tooltipRef,
  handlePointClick: (seriesData: LineData) => emit('pointClick', seriesData),
  ...toRefs(props),
});

const setSymbolChartDataLayer = () => {
  dataLayer.push({
    event: 'symbolChartClicked',
  });
};

const timestampToDate = (timestamp: Time) => {
  return new Date((timestamp as number) * 1000);
};

const getDateFormat = (filter: string) =>
  SYMBOL_LINE_CHART_FILTERS[filter].dateFormat;

const handleDataLayerTimeout = () => {
  threeSecondsDataLayerTimeout.value = setTimeout(() => {
    dataLayer.push({
      event: 'threeSecondsSymbolChartHover',
    });
  }, 3000);

  fiveSecondsDataLayerTimeout.value = setTimeout(() => {
    dataLayer.push({
      event: 'fiveSecondsSymbolChartHover',
    });
  }, 5000);
};

const resetDataLayerTimeout = () => {
  if (threeSecondsDataLayerTimeout.value) {
    clearTimeout(threeSecondsDataLayerTimeout.value);
  }

  if (fiveSecondsDataLayerTimeout.value) {
    clearTimeout(fiveSecondsDataLayerTimeout.value);
  }
};
</script>
